import { useEffect, useState, useCallback, useRef } from 'react'
import { midiConfigAPI } from './api'

/* Server-side MIDI-config. Mappings + device-model reizen mee naar elk scherm;
   de input/output device-ids blijven in localStorage omdat die per machine verschillen.

   Strategie: localStorage is de snelle cache (instant render), de server is de
   waarheid. Bij mount halen we de server-versie op en schrijven die lokaal weg. */

export const MIDI_SCOPES = ['kempenvision', 'imagecompo', 'tournaments']

export async function fetchMidiConfig(scope) {
  const { data } = await midiConfigAPI.get(scope)
  return { model: data?.model ?? null, mappings: data?.mappings ?? {}, updated_at: data?.updated_at ?? null }
}

export async function pushMidiConfig(scope, { model, mappings }) {
  await midiConfigAPI.put(scope, { model: model ?? null, mappings: mappings ?? {} })
}

export async function resetMidiConfig(scope) {
  await midiConfigAPI.reset(scope)
}

/**
 * useMidiConfigSync — houdt lokale mappings en de server in sync.
 *
 * @param scope        'kempenvision' | 'imagecompo' | 'tournaments'
 * @param local        { mappings, model }  — huidige lokale staat
 * @param applyRemote  (mappings, model) => void — schrijf server-versie lokaal weg
 *
 * Retourneert { status, lastSync, pull, push, reset }.
 *   status: 'idle' | 'loading' | 'synced' | 'error' | 'local-only'
 */
export function useMidiConfigSync(scope, local, applyRemote) {
  const [status, setStatus]     = useState('idle')
  const [lastSync, setLastSync] = useState(null)
  const [error, setError]       = useState(null)
  const applyRef = useRef(applyRemote)
  useEffect(() => { applyRef.current = applyRemote }, [applyRemote])

  const pull = useCallback(async () => {
    setStatus('loading'); setError(null)
    try {
      const cfg = await fetchMidiConfig(scope)
      const hasRemote = cfg.mappings && Object.keys(cfg.mappings).length > 0
      if (hasRemote) {
        applyRef.current?.(cfg.mappings, cfg.model)
        setLastSync(cfg.updated_at)
        setStatus('synced')
      } else {
        // Server heeft nog niets — lokale config blijft leidend tot je opslaat.
        setStatus('local-only')
      }
      return cfg
    } catch (e) {
      setError(e.response?.data?.error ?? e.message)
      setStatus('error')
      return null
    }
  }, [scope])

  const push = useCallback(async () => {
    setStatus('loading'); setError(null)
    try {
      await pushMidiConfig(scope, local)
      setLastSync(new Date().toISOString())
      setStatus('synced')
      return true
    } catch (e) {
      setError(e.response?.data?.error ?? e.message)
      setStatus('error')
      return false
    }
  }, [scope, local])

  const reset = useCallback(async () => {
    setStatus('loading'); setError(null)
    try {
      await resetMidiConfig(scope)
      setStatus('local-only')
      setLastSync(null)
      return true
    } catch (e) {
      setError(e.response?.data?.error ?? e.message)
      setStatus('error')
      return false
    }
  }, [scope])

  // Bij mount één keer de server-versie ophalen.
  useEffect(() => { pull() }, [pull])

  return { status, lastSync, error, pull, push, reset }
}
